import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { AccountService } from '../../../../../../services/account/account.service';
import { EventTypeService } from '../../../../../../services/event-type/event-type.service';

import { EventPricingMatrix, EventType} from '../../../../../../models/event-type';
import { CallbackResult, CallbackResultStatus } from '../../../../../../utils';
import {
  RemoveEventTypePricingMatrixModalComponent
} from './modals/remove-event-type-pricing-matrix-modal/remove-event-type-pricing-matrix-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ScheduleType } from '../../../../../../models/event-type-enums';

@Component({
  selector: 'app-event-type-pricing-matrices',
  templateUrl: './event-type-pricing-matrices.component.html',
  styleUrls: ['./event-type-pricing-matrices.component.css']
})
export class EventTypePricingMatricesComponent implements OnInit {

  public eventType: EventType = null;
  public eventPricingMatrices: EventPricingMatrix[];

  public eventTypeNotFound = false;
  public eventPricingMatricesNotFound = false;
  public serverError: number = null;

  public loadingEventType = false;
  public loadingEventPricingMatrices = false;

  // Make browser history available to the template.
  public history: History;

  public modalResult: CallbackResult = null;

  // Create instance references to lookup functions and enums so they may be
  // called in the template.
  public readonly CallbackResultStatus = CallbackResultStatus;
  public readonly ScheduleType = ScheduleType;

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private accountService: AccountService,
    private eventTypeService: EventTypeService
  ) {}

  ngOnInit() {
    const routeParams = this.route.snapshot.params;
    const eventTypeIdInt = parseInt(routeParams.id, 10);
    if (isNaN(eventTypeIdInt)) {
      this.eventTypeNotFound = true;
      return;
    }

    this.loadEventType(eventTypeIdInt);
    this.loadEventPricingMatrices(eventTypeIdInt);

    // Make browser history available to the template.
    this.history = history;
  }

  public openRemoveMatrixModal(matrix: EventPricingMatrix) {
    this.modalResult = null;

    const modalRef = this.modalService.open(RemoveEventTypePricingMatrixModalComponent);
    modalRef.componentInstance.matrix = matrix;
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.modalResult = result;
      this.loadEventPricingMatrices(this.eventType.id);
    };
  }

  /**
   * Whether there are any outstanding API requests in progress.
   */
  public isLoading() {
    return (
      this.loadingEventType ||
      this.loadingEventPricingMatrices
    );
  }

  /**
   * Load the event type with the given ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventType(eventTypeId: number) {
    this.loadingEventType = true;

    this.eventTypeService
      .getEventType(eventTypeId)
      .then(eventType => {
        this.eventType = eventType;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventTypeNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventType = false; });
  }

  /**
   * Load the event pricing matrices with the given event type ID.
   *
   * @param eventTypeId - The API ID of the event type.
   */
  private loadEventPricingMatrices(eventTypeId: number) {
    this.loadingEventPricingMatrices = true;

    // TODO: Handle case of >100 pricing matrices.
    this.eventTypeService
      .getEventPricingMatrices(eventTypeId, 1, 100)
      .then(pricingMatricesResponse => {
        this.eventPricingMatrices = pricingMatricesResponse.results;
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else if (reason.status === 404) {
          this.eventPricingMatricesNotFound = true;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => { this.loadingEventPricingMatrices = false; });
  }
}
